import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { dashboardApi, importCandidatesApi, casesApi, emergencySafetyApi, homeCareApi, moneyApi, petsApi, propertyProfileApi } from "../services/api";
import { useAuth } from "../hooks/useAuth";

const MODULE_ACCENTS = {
  food: { accent: "#d97706", light: "#fef9e7", emoji: "🥬" },
  vault: { accent: "#0d9488", light: "#e8faf8", emoji: "🏡" },
  home_care: { accent: "#15806d", light: "#eaf8f4", emoji: "🛠️" },
  property_profile: { accent: "#16715f", light: "#e9f7f3", emoji: "🏠" },
  emergency_safety: { accent: "#a64d3e", light: "#fff1ed", emoji: "🛡️" },
  vehicles: { accent: "#3b82f6", light: "#eff6ff", emoji: "🚗" },
  utilities: { accent: "#f59e0b", light: "#fffbeb", emoji: "⚡" },
  events: { accent: "#8b5cf6", light: "#f5f3ff", emoji: "✈️" },
  timeline: { accent: "#7c3aed", light: "#f5f3ff", emoji: "🗓️" },
  subscriptions: { accent: "#10b981", light: "#ecfdf5", emoji: "🔁" },
  money_overview: { accent: "#1f6f4a", light: "#e4efe6", emoji: "📊" },
  people: { accent: "#e7614a", light: "#fef2ef", emoji: "👥" },
  family: { accent: "#db2777", light: "#fdf2f8", emoji: "🤝" },
  pets: { accent: "#b65f7c", light: "#faedf2", emoji: "🐾" },
};

const AREA_CONTENT = {
  home: {
    eyebrow: "Home", title: "Your property, organized",
    description: "The systems, supplies, records, and services that keep your household running.",
    items: [
      { key: "food", title: "Food & pantry", detail: "Pantry, meals, recipes, and shopping", path: "/app/food" },
      { key: "vault", title: "Home Vault", detail: "Warranties, receipts, manuals, and equipment", path: "/app/home-vault" },
      { key: "home_care", title: "Home Care", detail: "Maintenance, repairs, projects, and trusted pros", path: "/app/home-care" },
      { key: "property_profile", title: "Property Profile", detail: "Rooms, systems, materials, and home facts", path: "/app/property-profile" },
      { key: "emergency_safety", title: "Emergency & Safety", detail: "Plans, shutoffs, safety checks, and contacts", path: "/app/emergency-safety" },
      { key: "vehicles", title: "Vehicles", detail: "Service, mileage, renewals, and ownership records", path: "/app/vehicles" },
    ],
  },
  plans: {
    eyebrow: "People & plans", title: "Everyone and everything ahead",
    description: "People, occasions, travel, and the shared timeline in one calm planning area.",
    items: [
      { key: "people", title: "People & gifts", detail: "People, occasions, interests, and gift planning", path: "/app/people" },
      { key: "events", summaryKey: "timeline", title: "Events & trips", detail: "Events, itineraries, packing, and readiness", path: "/app/events" },
      { key: "timeline", title: "Timeline", detail: "Every important household date in one view", path: "/app/timeline" },
      { key: "pets", title: "Pets", detail: "Profiles, routines, appointments, and care", path: "/app/pets" },
    ],
  },
  money: {
    eyebrow: "Money", title: "Recurring costs, made clear",
    description: "See obligations, price changes, renewals, and household spending signals before they surprise you.",
    items: [
      { key: "money_overview", title: "Household budget", detail: "Monthly spending, budgets, trends, and every money category", path: "/app/money/overview" },
      { key: "utilities", title: "Bills & utilities", detail: "Amounts due, usage changes, rates, and payment history", path: "/app/utilities" },
      { key: "subscriptions", title: "Subscriptions", detail: "Renewals, increases, trials, and cancellation decisions", path: "/app/subscriptions" },
    ],
  },
};

const LOADERS = {
  food: () => dashboardApi.widgetFood(), vault: () => dashboardApi.widgetVault(),
  vehicles: () => dashboardApi.widgetVehicles(), utilities: () => dashboardApi.widgetUtilities(),
  people: () => dashboardApi.widgetOccasions(), timeline: () => dashboardApi.widgetTimeline(),
  subscriptions: () => dashboardApi.widgetSubs(),
  home_care: () => homeCareApi.snapshot(),
  property_profile: () => propertyProfileApi.snapshot(),
  emergency_safety: () => emergencySafetyApi.snapshot(),
  money_overview: () => moneyApi.overview(),
  pets: () => petsApi.snapshot(),
};

function statusFor(key, data) {
  if (key === "family") return "Family plan active";
  if (!data) return "Summary unavailable";
  if (key === "food") return `${Number(data.total_items || 0)} items · ${Number(data.expiring_soon ?? data.expiring_7d ?? 0)} expiring`;
  if (key === "vault") return `${Number(data.total_items || 0)} records · ${Number(data.action_needed || 0)} need review`;
  if (key === "home_care") return `${Number(data.summary?.due_now || 0)} due · ${Number(data.summary?.upcoming || 0)} upcoming`;
  if (key === "property_profile") return `${Number(data.summary?.spaces || 0)} spaces · ${Number(data.completeness || 0)}% complete`;
  if (key === "emergency_safety") return `${Number(data.readiness || 0)}% ready · ${Number(data.summary?.checks_due || 0)} checks due`;
  if (key === "vehicles") return `${Array.isArray(data.vehicles) ? data.vehicles.length : 0} vehicles tracked`;
  if (key === "utilities") return `$${Number(data.monthly_total || 0).toFixed(0)}/mo · ${Number(data.spike_count || 0)} spikes`;
  if (key === "people") return `${Number(data.people_count || 0)} people · ${Array.isArray(data.occasions) ? data.occasions.length : 0} upcoming`;
  if (key === "events" || key === "timeline") return `${Array.isArray(data.events) ? data.events.length : 0} dates ahead`;
  if (key === "subscriptions") return `$${Number(data.monthly_total || 0).toFixed(0)}/mo · ${Number(data.active_count || 0)} active`;
  if (key === "money_overview") return `$${Number(data.summary?.expected || 0).toFixed(0)} expected this month`;
  if (key === "pets") return `${Number(data.summary?.pets || 0)} pets · ${Number(data.summary?.due || 0)} care items due`;
  return "Open section";
}

const AREA_ASK_PROMPTS = {
  home: ["What needs attention at home?", "What should I maintain next?"],
  plans: ["What is coming up?", "What should I prepare for?"],
  money: ["What bills or renewals are next?", "Where can I save?"],
};

function AreaAskBar({ area, label }) {
  const prompts = AREA_ASK_PROMPTS[area] || [];
  const openAsk = (question = "") => window.dispatchEvent(new CustomEvent("hm_open_ask", {
    detail: {
      question,
      context: {
        module: area,
        module_label: label,
        surface: `${label} navigation`,
      },
    },
  }));

  return <div className="fd-food-askbar hm-area-askbar" aria-label={`Ask HouseMind about ${label}`}>
    <input
      className="fd-food-askbar__input"
      placeholder={`✦ Ask about ${label.toLowerCase()}...`}
      onFocus={() => openAsk("")}
      readOnly
    />
    <div className="fd-food-askbar__chips">
      {prompts.map(prompt => <button type="button" className="fd-food-askbar__chip" key={prompt} onClick={() => openAsk(prompt)}>{prompt}</button>)}
    </div>
    <button type="button" className="fd-food-askbar__button" onClick={() => openAsk("")}>✦ Ask</button>
  </div>;
}

export default function NavigationAreaPage({ area }) {
  const { user } = useAuth();
  const hasFamilyPlan = user?.plan === "family" || user?.workspace?.plan === "family" || user?.workspaces?.some(workspace => workspace.plan === "family");
  const content = AREA_CONTENT[area] || AREA_CONTENT.home;
  const items = useMemo(() => (
    area === "plans" && hasFamilyPlan
      ? [...content.items, { key: "family", title: "Family sharing", detail: "Household access, invitations, and shared coordination", path: "/app/family" }]
      : content.items
  ), [area, content.items, hasFamilyPlan]);
  const uniqueKeys = useMemo(() => [...new Set(items.map(item => item.summaryKey || item.key).filter(key => LOADERS[key]))], [items]);
  const [summaries, setSummaries] = useState({});
  const [loading, setLoading] = useState(uniqueKeys.length > 0);
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(uniqueKeys.length > 0);
    Promise.all(uniqueKeys.map(async key => [key, await LOADERS[key]().catch(() => null)]))
      .then(rows => { if (active) setSummaries(Object.fromEntries(rows)); })
      .finally(() => { if (active) setLoading(false); });
    Promise.all([importCandidatesApi.count().catch(() => null), casesApi.list("active").catch(() => [])])
      .then(([discoveries, cases]) => { if (active) setOverview({ discoveries: Number(discoveries?.pending_count || 0), cases: Array.isArray(cases) ? cases.length : 0 }); });
    return () => { active = false; };
  }, [uniqueKeys]);

  return <main className={`hm-area-page hm-area-page--${area}`}>
    <header className="hm-area-hero">
      <span>{content.eyebrow}</span><h1>{content.title}</h1><p>{content.description}</p>
      {overview && <div className="hm-area-hero__status">
        <b className={overview.discoveries > 0 ? "has-count" : ""}>{overview.discoveries ? `${overview.discoveries} discoveries waiting` : "Discoveries clear"}</b>
        <b className={overview.cases > 0 ? "has-count" : ""}>{overview.cases ? `${overview.cases} open cases` : "No open cases"}</b>
      </div>}
    </header>
    <section className="hm-area-grid" aria-label={`${content.eyebrow} sections`}>
      {items.map((item, index) => {
        const accent = MODULE_ACCENTS[item.key] || {};
        const summaryKey = item.summaryKey || item.key;
        return <Link className="hm-area-card" to={item.path} key={`${area}-${item.path}-${item.title}-${index}`} style={{ "--card-accent": accent.accent, "--card-accent-light": accent.light }}>
          <span className="hm-area-card__icon" aria-hidden="true">{accent.emoji}</span>
          <span className="hm-area-card__copy"><strong>{item.title}</strong><small>{item.detail}</small><em>{item.key !== "family" && loading ? "Checking household records…" : statusFor(item.key, summaries[summaryKey])}</em></span>
          <span className="hm-area-card__arrow" aria-hidden="true">›</span>
        </Link>;
      })}
    </section>
    <aside className="hm-area-note"><span>Verified context</span><p>Summaries come from the same module records you already use. Open a section for full details and actions.</p></aside>
    <AreaAskBar area={area} label={content.eyebrow} />
  </main>;
}
